import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { deviceAPI, projectAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from '../utils/useIsAdmin';

interface Device {
  id: string;
  name: string;
  label?: string | null;
  deviceId: string;
  status: string;
  lastSeen: string;
  created_at: string;
  project_name?: string;
  updatedAt: string;
  projectId?: string | null;
}

interface Project {
  id: string;
  name: string;
  description?: string;
}

const Devices: React.FC = () => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [devices, setDevices] = useState<Device[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editProjectId, setEditProjectId] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await deviceAPI.getAllDevices();
      if (response.data.success) {
        setDevices(response.data.data);
      } else {
        setError(t('devices.failedToFetchDevices'));
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError(t('devices.failedToFetchDevices'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectAPI.getAllProjects();
      if (response.data.success) {
        setProjects(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, []);

  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device);
    setEditStatus(device.status);
    setEditProjectId(device.projectId || '');
    setEditLabel(device.label || '');
    setEditDialogOpen(true);
  };

  const handleUpdateDevice = async () => {
    if (!selectedDevice) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // Update device status
      const statusResponse = await deviceAPI.updateDeviceStatus(selectedDevice.id, editStatus);
      
      if (statusResponse.data.success) {
        // Update device label
        await deviceAPI.updateDeviceLabel(selectedDevice.id, editLabel.trim() === '' ? null : editLabel.trim());
        // Update device project assignment
        const projectId = editProjectId && editProjectId.trim() !== '' ? editProjectId : null;
        const projectResponse = await deviceAPI.updateDeviceProject(selectedDevice.id, projectId);
        
        if (projectResponse.data.success) {
          // Update the device in the list with the response data which includes project_name
          const updatedDeviceData = projectResponse.data.data;
          setDevices(devices.map(device => 
            device.id === selectedDevice.id 
              ? { 
                  ...device, 
                  status: editStatus,
                  label: editLabel.trim() === '' ? null : editLabel.trim(),
                  projectId: updatedDeviceData.projectId,
                  project_name: updatedDeviceData.project_name
                }
              : device
          ));
          setEditDialogOpen(false);
          setSelectedDevice(null);
          setSuccess(t('devices.deviceUpdatedSuccessfully'));
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);
        } else {
          // Handle project assignment failure
          console.error('Project assignment failed:', projectResponse.data.error);
          setError(`${t('devices.failedToAssignProject')}: ${projectResponse.data.error || t('common.unknown')}`);
          // Refresh data to get the current state from server
          await fetchDevices();
        }
      } else {
        // Handle status update failure
        console.error('Status update failed:', statusResponse.data.error);
        setError(`${t('devices.failedToUpdateStatus')}: ${statusResponse.data.error || t('common.unknown')}`);
      }
    } catch (err) {
      console.error('Error updating device:', err);
      setError(t('devices.failedToUpdateDevice'));
      // Refresh data to get the current state from server
      await fetchDevices();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm(t('devices.confirmDeleteDevice'))) return;

    try {
      const response = await deviceAPI.deleteDevice(deviceId);
      if (response.data.success) {
        setDevices(devices.filter(device => device.id !== deviceId));
      }
    } catch (err) {
      console.error('Error deleting device:', err);
      setError(t('devices.failedToDeleteDevice'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    fetchDevices();
    fetchProjects();
  }, [fetchDevices, fetchProjects]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('devices.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDevices}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('devices.registeredDevices')} ({devices.length})
          </Typography>
          
          {devices.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              {t('devices.noDevicesRegistered')}
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                  <TableCell>{t('devices.label')}</TableCell>
                    <TableCell>{t('devices.deviceName')}</TableCell>
                    <TableCell>{t('devices.deviceId')}</TableCell>
                    <TableCell>{t('devices.status')}</TableCell>
                    <TableCell>{t('common.project')}</TableCell>
                    <TableCell>{t('devices.lastSeen')}</TableCell>
                    <TableCell>{t('devices.registered')}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.id}>
                    <TableCell>
                      {device.label ? (
                        device.label
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('devices.noLabel')}
                        </Typography>
                      )}
                    </TableCell>
                      <TableCell>{device.name}</TableCell>
                      <TableCell>{device.deviceId}</TableCell>
                      <TableCell>
                        <Chip 
                          label={device.status} 
                          color={getStatusColor(device.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {device.projectId ? (
                          device.project_name
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('devices.noProjectAssigned')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(device.lastSeen)}</TableCell>
                      <TableCell>{formatDate(device.created_at)}</TableCell>
                      <TableCell>
                        {isAdmin && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditDevice(device)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDevice(device.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Device Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('devices.editDevice')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('devices.label')}
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label={t('common.status')}
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
            margin="normal"
          >
            <MenuItem value="ACTIVE">{t('devices.active')}</MenuItem>
            <MenuItem value="PENDING">{t('devices.pendingApproval')}</MenuItem>
            <MenuItem value="REJECTED">{t('devices.rejected')}</MenuItem>
          </TextField>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('common.project')}</InputLabel>
            <Select
              value={editProjectId}
              onChange={(e) => setEditProjectId(e.target.value)}
              label={t('common.project')}
            >
              <MenuItem value="">
                <em>{t('devices.noProjectAssigned')}</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isUpdating}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleUpdateDevice} 
            variant="contained" 
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {isUpdating ? t('devices.updating') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Devices;
